import { FC, Fragment, useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../../hooks';
import { Helmet } from 'react-helmet-async';
import PageTitleWrapper from '../../../components/PageTitleWrapper';
import LedgerSummaryHeader from './PageHeader';
import PageContainerWrapper from '../../../components/PageContainerWrapper';
import { Table, TableBody, TableCell, TableHead, TableRow, Typography, Paper, Link, Tooltip, IconButton, TableContainer, useTheme } from '@mui/material';
import { currencyFormatter } from '../../../utils';
import { tableStyles } from '../Reports';
import { PartyLedgerData } from './types';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useSearchParams } from 'react-router-dom';
import { getLedgerSummaryDataAction } from './actions';

export interface LedgerSummaryProps {}

export const LedgerSummary: FC<LedgerSummaryProps> = ({}) => {
  const classes = tableStyles();
  const theme = useTheme();
  const dispatchAction = useAppDispatch();
  const { data: partyLedgerData } = useAppSelector(
    (state) => state.ledgerSummaryState
  );
  const [searchParams] = useSearchParams();
  const partyId = searchParams.get('party_id');

  useEffect(() => {
    dispatchAction(getLedgerSummaryDataAction(partyId || undefined));
  }, [dispatchAction, partyId]);

  let count = 0;
  return (
    <>
      <Helmet>
        <title>Ledger Summary</title>
      </Helmet>
      <PageTitleWrapper>
        <LedgerSummaryHeader partyId={partyId || undefined} />
      </PageTitleWrapper>
      <PageContainerWrapper>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)' }}>
          <Table aria-label="ledger summary report" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell 
                  width={20}
                  sx={{ 
                    backgroundColor: theme.palette.primary.main,
                    borderRight: '0.5px solid #ccc',
                    borderLeft: '0.5px solid #ccc',
                    fontWeight: 600,
                    color: theme.palette.primary.contrastText
                  }}
                >
                  S.N.
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: theme.palette.primary.main,
                    borderRight: '0.5px solid #ccc',
                    fontWeight: 600,
                    color: theme.palette.primary.contrastText
                  }}
                >
                  Site Address
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: theme.palette.primary.main,
                    borderRight: '0.5px solid #ccc',
                    fontWeight: 600,
                    color: theme.palette.primary.contrastText
                  }}
                >
                  Site No
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: theme.palette.primary.main,
                    borderRight: '0.5px solid #ccc',
                    fontWeight: 600,
                    color: theme.palette.primary.contrastText
                  }}
                >
                  Billing
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: theme.palette.primary.main,
                    borderRight: '0.5px solid #ccc',
                    fontWeight: 600,
                    color: theme.palette.primary.contrastText
                  }}
                >
                  Payment(Inc tds)
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: theme.palette.primary.main,
                    fontWeight: 600,
                    color: theme.palette.primary.contrastText
                  }}
                >
                  Balance
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {partyLedgerData &&
                partyLedgerData.map((partyData, partyIndex) => (
                  <Fragment key={partyIndex}>
                    {/* Party Header Row */}
                    <TableRow 
                      sx={{ 
                        backgroundColor: '#f5f5f5',
                        '& .MuiTableCell-root': {
                          borderBottom: '1px solid #e0e0e0',
                          fontWeight: 600,
                          fontSize: '1rem'
                        }
                      }}
                    >
                      <TableCell colSpan={6} sx={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <Typography
                            variant="h6"
                            color="primary"
                            sx={{ fontWeight: 600 }}
                          >
                            {!partyId ? (
                              <a
                                href={`${window.location.pathname}?party_id=${partyData.partyId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: '#1976d2',
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                  fontSize: '1rem'
                                }}
                              >
                                {partyData.party}
                              </a>
                            ) : (
                              partyData.party
                            )}
                          </Typography>
                          <Link
                            href={`https://web.whatsapp.com/send?phone=${partyData.mobile}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: '#25D366',
                              textDecoration: 'none',
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              '&:hover': {
                                textDecoration: 'underline',
                                color: '#128C7E'
                              }
                            }}
                          >
                            {partyData.mobile}
                          </Link>
                        </div>
                      </TableCell>
                    </TableRow>
                    
                    {/* Site Rows for this Party */}
                    {partyData.sites.map((site, siteIndex) => (
                      <TableRow 
                        key={`${partyIndex}-${siteIndex}`}
                        sx={{ height: 52 }}
                      >
                        <TableCell sx={{ borderRight: '0.5px solid #ccc' }}>{++count}</TableCell>
                        <TableCell sx={{ borderRight: '0.5px solid #ccc' }}>
                          {site.siteAddress}
                        </TableCell>
                        <TableCell sx={{ borderRight: '0.5px solid #ccc' }}>
                          <Tooltip arrow title="Go to site details">
                            <Link
                              color={'#0000ff'}
                              underline="always"
                              target="_blank"
                              rel="noopener noreferrer"
                              href={`/slips/view-slips/${site.siteId}/${partyData.partyId}?tab_index=7`}
                            >
                              {site.siteId}
                            </Link>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ borderRight: '0.5px solid #ccc' }}>
                          {currencyFormatter.format(site.invoicesTotal)}
                        </TableCell>
                        <TableCell sx={{ borderRight: '0.5px solid #ccc' }}>
                          {currencyFormatter.format(site.totalPayment)}
                        </TableCell>
                        <TableCell>
                          {currencyFormatter.format(site.balance)}
                        </TableCell>
                      </TableRow>
                    ))}
                    
                    {/* Party Total Row */}
                    <TableRow 
                      sx={{ 
                        backgroundColor: '#fff',
                        fontWeight: 'bold',
                        '& .MuiTableCell-root': {
                          borderTop: '0px solid #ccc'
                        }
                      }}
                    >
                      <TableCell sx={{ borderRight: '0.5px solid #ccc' }}></TableCell>
                      <TableCell sx={{ borderRight: '0.5px solid #ccc' }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Total</Typography>
                      </TableCell>
                      <TableCell sx={{ borderRight: '0.5px solid #ccc' }}></TableCell>
                      <TableCell sx={{ borderRight: '0.5px solid #ccc' }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {currencyFormatter.format(partyData.totalInvoicesForParty)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ borderRight: '0.5px solid #ccc' }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {currencyFormatter.format(partyData.totalPaymentsForParty)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 'bold',
                            color: partyData.balanceForParty <= 0 ? '#4caf50' : '#f44336'
                          }}
                        >
                          {currencyFormatter.format(partyData.balanceForParty)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </PageContainerWrapper>
    </>
  );
};

export default LedgerSummary;
